import React, { useEffect, useMemo, useState } from 'react';

import {
  Badge,
  ChakraBox,
  ChakraFlex,
  ChakraSpacer,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  RowObject,
  Table,
  TableColumn,
  UserAvatarCard,
  ProviderAvatarBlock,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import IconText from '~/components/common/IconText';
import PatientTableCellToolTip from '~/components/common/PatientTableCellToolTip';
import { ApiCommand } from '~/lib/Api';
import {
  OBSERVATION_GOAL_ACTIVITY_TYPE,
  PatientActivityFilterType,
  defaultActivityFilterState,
} from '~/lib/constants/patientActivity.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import { getPreferredFullName } from '~/lib/util/patient.util';
import {
  formatDisplayValue,
  getActivityChangeKeys,
  getDisplayFieldsList,
  getDisplayFieldsListLabel,
  isThisYear,
} from '~/lib/util/patientActivity.util';
import util from '~/lib/util/util';
import Loading from '~/pages/loading';
import PatientActivityActionBar from './PatientActivityActionBar';
import { activityTypeMap } from './util';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

const HEADER_HEIGHT = '180px';
const PATIENT = 'Patient';

const { labelBold, label, captionBold } = EquipUITheme.typography;
const { primary } = EquipUIFireflyV2Theme.colors;

type PatientActivityRow =
  | {
      activityFor: React.ReactNode;
      dateAndTime: React.ReactNode;
      field?: React.ReactNode;
      newValue?: React.ReactNode;
      originalValue?: React.ReactNode;
      parentType: React.ReactNode;
      user: React.ReactNode;
    }
  | { customRow: React.ReactNode };
enum CreatedByType {
  PROVIDER = 'provider',
  PATIENT = 'patient',
  SUPPORT = 'support',
}
type CreatedByUserType = {
  authIdWithoutPrefix: string;
  chosenName?: string;
  externalId: string;
  firstName: string;
  lastName: string;
  providerType: string;
  type: CreatedByType;
  displayFirstName?: string;
  displayLastName?: string;
  providerRoleAtTheTimeOfAction?: string;
  avatar?: AvatarDetails;
};
type PatientActivityListObject = {
  after: Record<string, any>;
  before: Record<string, any>;
  createdAt: string;
  createdBy: CreatedByUserType;
  filterType: string;
  operator: string;
  subject: string;
  type: string;
};
interface PatientActivityProps {
  patientId: string;
}

const TOP_LEVEL_ID = 'patient-activity-table';

const PatientActivityTable = ({ patientId }: PatientActivityProps) => {
  const [filterState, setFilterState] = useState<PatientActivityFilterType>(
    defaultActivityFilterState,
  );
  const [activityList, setActivityList] = useState<PatientActivityListObject[]>(
    [],
  );
  const {
    data: patientAdminEpisodeActivity,
    loading: adminEpisodeActivityLoading,
    sendRequest: getAdminEpisodeActivity,
  } = useApi<PatientActivityListObject[]>();

  const {
    data: patientBasicInfoActivity,
    loading: basicInfoActivityLoading,
    sendRequest: getBasicInfoActivity,
  } = useApi<PatientActivityListObject[]>();

  useEffect(() => {
    if (patientAdminEpisodeActivity && patientBasicInfoActivity) {
      const filteredActivityList = [
        ...patientAdminEpisodeActivity,
        ...patientBasicInfoActivity,
      ]?.filter(({ type }) => type !== OBSERVATION_GOAL_ACTIVITY_TYPE);
      setActivityList(filteredActivityList);
    }
  }, [patientAdminEpisodeActivity, patientBasicInfoActivity]);
  const fetchPatientActivity = (): void => {
    const filterParams = {};
    const { fromDate, toDate } = filterState;

    if (fromDate) {
      filterParams['from-date'] = DateTime.fromFormat(fromDate, 'yyyy-LL-dd')
        .startOf('day')
        .toISO();
    }
    if (toDate) {
      filterParams['to-date'] = DateTime.fromFormat(toDate, 'yyyy-LL-dd')
        .endOf('day')
        .toISO();
    }
    getAdminEpisodeActivity({
      command: ApiCommand.GET,
      options: {
        'filter-by': filterParams,
      },
      url: urlConstants.clinical_data.getActivitiesByPatientId(patientId),
    });
    getBasicInfoActivity({
      command: ApiCommand.GET,
      options: {
        'filter-by': filterParams,
      },
      url: urlConstants.patients.getActivitiesByPatientId(patientId),
    });
  };
  useEffect(() => {
    fetchPatientActivity();
  }, [filterState]);

  const patientActivityRowObjects = useMemo<RowObject<PatientActivityRow>[]>(
    () =>
      activityList
        .sort(
          (a, b) =>
            Number(new Date(b.createdAt)) - Number(new Date(a.createdAt)),
        )
        .map((patientActivityObj: PatientActivityListObject) => {
          const {
            subject,
            after,
            before,
            createdAt,
            createdBy,
            filterType,
            operator,
            type,
          } = patientActivityObj;
          const isCustomRow = operator === 'NEW_FEATURE';
          const {
            chosenName: createdByChosenName,
            externalId: createdByExternalId,
            firstName: createdByFirstName,
            lastName: createdByLastName,
            type: createdByUserType,
            displayFirstName: createdByDisplayFirstName,
            displayLastName: createdByDisplayLastName,
            providerRoleAtTheTimeOfAction:
              createdByProviderTypeAtTheTimeOfAction,
            avatar: createdByAvatar,
          } = createdBy || {};
          const isDisplayName =
            createdByDisplayFirstName && createdByDisplayLastName;
          const rowKey = `RowObject::${createdByExternalId}::${createdAt}::${type}`;
          const getParentTypeCell = () => (
            <ChakraBox
              id={`${rowKey}-filter-type`}
              marginTop={!isCustomRow ? '8px' : '0px'}
            >
              <IconText
                content={[activityTypeMap[filterType]?.label]}
                contentColor={primary[100]}
                icon={activityTypeMap[filterType]?.icon}
              />
            </ChakraBox>
          );
          const getTypeCell = () => (
            <ChakraBox
              id={`${rowKey}-type`}
              marginTop={!isCustomRow ? '8px' : '0px'}
            >
              <IconText
                content={[activityTypeMap[type]?.label]}
                contentColor={primary[100]}
                icon={activityTypeMap[type]?.icon}
                isContentBold
              />
            </ChakraBox>
          );
          const createdAtDateTime = DateTime.fromISO(createdAt);
          const activityDate = isThisYear(createdAt)
            ? `${createdAtDateTime.monthLong} ${createdAtDateTime.day}`
            : createdAtDateTime.toLocaleString(DateTime.DATE_MED);

          const getDateTimeFieldCell = () => {
            const activityTime = createdAtDateTime.toLocaleString(
              DateTime.TIME_SIMPLE,
            );
            return (
              <ChakraBox
                id={`${rowKey}-date-and-time`}
                marginTop={!isCustomRow ? '8px' : '0px'}
              >
                <ChakraText
                  as="span"
                  {...labelBold}
                  color="neutral.primary"
                  marginRight="8px"
                >
                  {activityDate}
                </ChakraText>
                {!isCustomRow && (
                  <ChakraText as="span" {...label} color="neutral.secondary">
                    {activityTime}
                  </ChakraText>
                )}
              </ChakraBox>
            );
          };
          if (isCustomRow) {
            const getCustomRow = () => (
              <ChakraFlex
                backgroundColor="tertiary.marigold.background"
                border="2px solid"
                borderColor="neutral.background.secondary"
                borderRadius="12px"
                id={`${TOP_LEVEL_ID}-${type}-new-feature`}
                marginLeft="-16px"
                padding="16px"
              >
                <Badge
                  badgeColor="tertiary.marigold.base"
                  id={`${TOP_LEVEL_ID}__user-count`}
                  margin="0px 16px 0px 0px"
                >
                  <ChakraText {...captionBold} color="neutral.primary">
                    New feature
                  </ChakraText>
                </Badge>
                {getTypeCell()}
                <ChakraText {...label} marginLeft="8px">
                  {` added to activity type ${activityDate}th*`}
                </ChakraText>
                <ChakraSpacer />
                <ChakraFlex>
                  <ChakraText {...label}>
                    * Previous changes can’t be displayed before this date.
                  </ChakraText>
                </ChakraFlex>
              </ChakraFlex>
            );
            return {
              isCustomRow: true,
              key: `RowObject::${type}::${operator}::${createdAt}`,
              object: {
                customRow: getCustomRow(),
              },
              value: `${type}${operator}`,
            };
          }
          const activityChangeKeys = getActivityChangeKeys(before, after);
          const fieldArrLabel: {
            key: string;
            value: string;
          }[] = getDisplayFieldsListLabel(
            activityTypeMap[type]?.mapper,
            activityChangeKeys,
          );
          const fieldArr: string[] = getDisplayFieldsList(
            activityTypeMap[type]?.mapper,
            activityChangeKeys,
          );

          const originalValArr = fieldArr.map((key) => ({
            key,
            value: formatDisplayValue(key, before?.[key]),
          }));
          const newValArr = fieldArr.map((key) => ({
            key,
            value: formatDisplayValue(key, after?.[key]),
          }));
          const getListCell = (
            list: { key: string; value: string }[],
            listType: string,
          ) => (
            <ChakraBox id={`${rowKey}-${listType}`} marginY="8px">
              {(list ?? []).map(({ key, value }) => (
                <PatientTableCellToolTip
                  content={value || ''}
                  id={`${key}-${value}`}
                  key={`${key}-${value}`}
                >
                  <ChakraText
                    id={`${key}-${value}__text`}
                    marginBottom="8px"
                    maxWidth="125px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {value}
                  </ChakraText>
                </PatientTableCellToolTip>
              ))}
            </ChakraBox>
          );

          const getActivityForCell = () => {
            const activityForValue = util.capitalize(subject) || PATIENT;
            return (
              <PatientTableCellToolTip
                content={activityForValue || ''}
                id={`${rowKey}-activity-for`}
              >
                <ChakraText
                  maxWidth="125px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {activityForValue}
                </ChakraText>
              </PatientTableCellToolTip>
            );
          };
          return {
            key: rowKey,
            object: {
              activityFor: getActivityForCell(),
              dateAndTime: getDateTimeFieldCell(),
              field: getListCell(fieldArrLabel, 'field'),
              newValue: getListCell(newValArr, 'after'),
              originalValue: getListCell(originalValArr, 'before'),
              parentType: getParentTypeCell(),
              user: (
                <ChakraBox marginTop={!isCustomRow ? '8px' : '0px'}>
                  {createdBy &&
                    (createdByUserType === CreatedByType.PROVIDER ? (
                      <ProviderAvatarBlock
                        avatarUrl={createdByAvatar?.thumbnailUrl}
                        displayName={
                          isDisplayName && {
                            firstName: createdByDisplayFirstName,
                            lastName: createdByDisplayLastName,
                          }
                        }
                        id={`${TOP_LEVEL_ID}__host-avatar-${createdByExternalId}`}
                        legalName={{
                          firstName: createdByFirstName,
                          lastName: createdByLastName,
                        }}
                        providerTypeName={
                          createdByProviderTypeAtTheTimeOfAction
                        }
                        shouldShowProviderType
                        tooltipWrapper={({ children }) => (
                          <ProviderAvatarTooltipWrapper
                            actionTime={createdAt}
                            isRoleAtAction
                            providerExternalId={createdByExternalId}
                          >
                            {children}
                          </ProviderAvatarTooltipWrapper>
                        )}
                        variant="standard"
                      />
                    ) : (
                      <UserAvatarCard
                        id={`${rowKey}-user`}
                        primaryText={[
                          getPreferredFullName(
                            createdByChosenName,
                            createdByFirstName,
                            createdByLastName,
                          ) || 'System',
                        ]}
                        primaryTextFontSize="12px"
                        primaryTextLineHeight="16.8px"
                        secondaryText={[util.capitalize(createdByUserType)]}
                        secondaryTextStyle={{
                          fontSize: '10px',
                          lineHeight: '14px',
                        }}
                      />
                    ))}
                </ChakraBox>
              ),
            },

            value: createdByFirstName,
          };
        }),
    [activityList],
  );

  const activityTableHeads: TableColumn<PatientActivityRow>[] = [
    { header: 'Date and time', key: 'dateAndTime' },
    { header: 'User', key: 'user' },
    { header: 'Type', key: 'parentType' },
    { header: 'Activity for', key: 'activityFor' },
    { header: 'Field', key: 'field' },
    { header: 'Original value', key: 'originalValue' },
    { header: 'New value', key: 'newValue' },
  ] as TableColumn<PatientActivityRow>[];

  return (
    <ChakraBox
      height="100%"
      maxHeight={`calc(100vh - ${HEADER_HEIGHT})`}
      overflowY="hidden"
      width="100%"
    >
      <PatientActivityActionBar onFilterChange={setFilterState} />
      {!basicInfoActivityLoading && !adminEpisodeActivityLoading ? (
        <ChakraBox
          marginTop="23px"
          maxHeight={`calc(100vh - ${HEADER_HEIGHT} - 113px)`}
          overflowY="auto"
          paddingRight="24px"
        >
          <Table<PatientActivityRow>
            columns={activityTableHeads}
            id={TOP_LEVEL_ID}
            isTextVeticalAlignToTop
            rows={patientActivityRowObjects}
            variant="unstyled"
          />
        </ChakraBox>
      ) : (
        <Loading />
      )}
    </ChakraBox>
  );
};

export default PatientActivityTable;
